import type { FC } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { Language } from "@ui/components/LanguageSelector";
import { LanguageSelector } from "@ui/components/LanguageSelector";
import { SearchHeaderBanner } from "@ui/components/Search";
import { useBodyScrollLock } from "@ui/hooks/useBodyScrollLock";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import classNames from "classnames";

import { BurgerMenuIcon } from "@jti/brand-icons/burger-menu";
import { Container } from "@jti/core-components/components/Container";
import type { LanguageSelectorVariantConfig } from "@jti/fe-common";

import type { BreadcrumbsList } from "../Breadcrumbs";
import type { LogoProps } from "../Logo";
import { Logo } from "../Logo";

import { HeaderBreadcrumbs } from "./components/HeaderBreadcrumbs/HeaderBreadcrumbs";
import type { NavLinks } from "./components/Navigation";
import { Navigation } from "./components/Navigation";
import { SignOut } from "./components/SignOut/SignOut";
import type { ToolsProps } from "./components/Tools";
import { Tools } from "./components/Tools";
import { UserIndicationButtonMobile } from "./components/UserActions/components/UserIndicationButton/UserIndicationButtonMobile";
import { useBuilderHeaderStyle } from "./hooks/useBuilderHeaderStyle";
import { useHeaderScrollStore } from "./hooks/useHeaderScrollStore";
import { useOnScrollDown } from "./hooks/useOnScrollDown";

import { HeaderProvider } from "./context";

import logoStyles from "../Logo/Logo.module.scss";
import styles from "./Header.module.scss";

export type AccountDropdownSettings = {
  logoutRedirectPage?: string;
  logoutLabel?: string;
  greetingMessage?: string;
  hideGreetingMessage?: boolean;
  profileLink?: string;
  accountDashboardLabel?: string;
  accountDashboardLink?: string;
  ordersLabel?: string;
  ordersLink?: string;
  paymentsLabel?: string;
  paymentsLink?: string;
  accountInformationLabel?: string;
  accountInformationLink?: string;
  packCodesLink?: string;
  packCodesLabel?: string;
  loyaltyPointsCurrency?: string;
  tierTranslations?: Record<string, string>;
  hideLoyaltyTiers?: boolean;
};

export const PAGE_HEADER_ID = "page-header";

type HeaderProps = {
  isDarkMode?: boolean;
  logo: Omit<LogoProps, "isDarkMode">;
  hideNavigation?: boolean;
  isSimplified?: boolean;
  navLinks: NavLinks;
  languages: Language[];
  breadcrumbsList: BreadcrumbsList;
  renderSubmenu?: (id: string, isDarkMode?: boolean) => JSX.Element;
  accountLink?: string;
  accountLabel?: string;
  accountDropdownSettings?: AccountDropdownSettings;
  storeLocatorLink?: string;
  storeLocatorLabel?: string;
  overlayHeader?: boolean;
  onLogOut: () => void;
  withCart: boolean;
  useHeadingTagForBreadcrumbs?: boolean;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  transparentHeaderTheme?: "dark" | "light" | "inherit";
  variant?: LanguageSelectorVariantConfig;
  hideBreadcrumbsOnMobile?: boolean;
  currentPathname?: string;
  expandBreadcrumbsLabel?: string;
} & Omit<ToolsProps, "isDarkMode" | "onOpenSearch" | "onCloseSearch" | "isSearchOpened">;

export const Header: FC<HeaderProps> = ({
  isDarkMode = false,
  logo,
  isSimplified = false,
  hideNavigation = false,
  navLinks,
  accountLink,
  accountLabel,
  accountLoadingLabel,
  accountDropdownSettings,
  renderMiniCart = undefined,
  mobileAppLink,
  resultsLink,
  languages,
  storeLocatorLink,
  storeLocatorLabel,
  openMenuLabel,
  closeMenuLabel,
  breadcrumbsList,
  renderSubmenu,
  overlayHeader = false,
  withCart = false,
  useHeadingTagForBreadcrumbs,
  onLogOut,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  currentPathname,
  transparentHeaderTheme = "inherit",
  variant = {
    mobile: "FULL_LANGUAGE_NAME",
    desktop: "SHORT_LANGUAGE_NAME",
  },
  hideBreadcrumbsOnMobile = false,
  expandBreadcrumbsLabel,
}) => {
  const { isUserLoggedIn } = useUser();
  const { toggleScroll } = useBodyScrollLock(false);
  const [headerPortalHeight, setHeaderPortalHeight] = useState(0);
  const headerPortalRef = useRef<HTMLDivElement | null>(null);
  const headerPortalResizeObserverRef = useRef<ResizeObserver | null>(null);

  const setHeaderPortalRef = useCallback((node: HTMLDivElement | null) => {
    if (headerPortalResizeObserverRef.current) {
      headerPortalResizeObserverRef.current.disconnect();
      headerPortalResizeObserverRef.current = null;
    }
    headerPortalRef.current = node;
    if (node) {
      const resizeObserver = new ResizeObserver(() => {
        setHeaderPortalHeight(node.getBoundingClientRect().height);
      });
      resizeObserver.observe(node);
      headerPortalResizeObserverRef.current = resizeObserver;
    }
  }, []);

  const [isHydrated, setIsHydrated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [navigationOpened, setNavigationOpened] = useState(false);
  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const headerRef = useRef<HTMLHeadElement | null>(null);
  const headerHeight = headerRef.current?.getBoundingClientRect()?.height || 0;

  const tabletUserNameRef = useRef<HTMLDivElement | null>(null);

  const [topBannersHeight, setTopBannersHeight] = useState(0);
  const { mainBreakpoint } = useCurrentBreakpoint();
  const isMobile = ["xs", "sm"].includes(mainBreakpoint);
  const hideBreadcrumbs = hideBreadcrumbsOnMobile && isMobile;

  useEffect(() => {
    if (document.readyState === "complete") {
      setIsHydrated(true);
    } else {
      const handleLoad = () => setIsHydrated(true);
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    const readTopBannersHeight = () => {
      const root = document.documentElement;
      const rawHeight = getComputedStyle(root).getPropertyValue("--top-banners-height");
      const height = parseInt(rawHeight.trim(), 10);
      setTopBannersHeight(height || 0);
    };

    readTopBannersHeight();

    const observer = new MutationObserver((mutations) => {
      for (const { type, attributeName } of mutations) {
        if (type === "attributes" && attributeName === "style") {
          readTopBannersHeight();
          return;
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
      subtree: false,
    });

    return () => observer.disconnect();
  }, []);

  const scrollState = useHeaderScrollStore({
    headerHeight,
    topBannersHeight,
    headerPortalHeight,
    overlayHeader,
  });

  const { isAtTop, isScrolled, isVisible, isHidden, isTransitionDisabled, isVisibleOverlay } = scrollState;

  const isBuilderStyleSkipped = useBuilderHeaderStyle({
    headerPortalHeight,
    isVisible: (isVisible || isAtTop) && !overlayHeader,
    isVisibleOverlay,
    isTransitionDisabled,
    isOverlayHeader: overlayHeader,
    isHidden,
    styles,
  });

  const shouldAddFixedStyle = isBuilderStyleSkipped && overlayHeader;

  useOnScrollDown(isSearchOpened, () => setIsSearchOpened(false));

  useEffect(() => {
    if (!isNaN(headerHeight)) {
      document.documentElement.style.setProperty("--header-portal-height", `-${headerHeight - headerPortalHeight}px`);
    }
  }, [headerHeight, headerPortalHeight]);

  // This useLayoutEffect sets scroll-padding-top CSS property accounting for sticky header when Next.js
  // scrolls page content into view right after navigation. Implements officially proposed solution:
  // https://nextjs.org/docs/pages/api-reference/components/link#scroll-offset-with-sticky-headers
  // useLayoutEffect() was used because useEffect() would apply new scroll-padding-top value after
  // Next.js runs its <Link> scroll behavior, resulting in scrolling to incorrect point when height
  // of the header on page where user navigated from differs from header on page user navigated to.
  useLayoutEffect(() => {
    const currentHeaderHeight = headerRef.current?.getBoundingClientRect()?.height || 0;
    document.documentElement.style.setProperty("scroll-padding-top", `${currentHeaderHeight - headerPortalHeight}px`);
  }, [headerHeight, headerPortalHeight]);

  useEffect(() => {
    toggleScroll(navigationOpened);
  }, [navigationOpened, toggleScroll]);

  const handleHamburgerClick = () => {
    setNavigationOpened((navigationOpened) => !navigationOpened);
  };

  const openSearch = () => {
    setIsSearchOpened(true);
  };

  const closeSearch = () => {
    setIsSearchOpened(false);
  };

  const hasBackground = isSearchOpened || navigationOpened || (isScrolled && overlayHeader);
  const isTransparent = isHydrated && isAtTop && overlayHeader && !isHovered;

  function getTheme(): "light" | "dark" {
    if (!hasBackground && isTransparent && transparentHeaderTheme !== "inherit") {
      return transparentHeaderTheme;
    }
    return isDarkMode ? "dark" : "light";
  }

  const theme = getTheme();
  return (
    <HeaderProvider>
      <header
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={classNames(styles.headerPosition, styles.header, {
          "dark-theme": theme === "dark",
          "light-theme": isTransparent && theme === "light",
          [styles.hover]: isHovered,
          [styles.transparent]: isHydrated && isAtTop && overlayHeader,
          [styles.withBackground]: hasBackground,
          [styles.hidden]: isHidden,
          [styles.visible]: (isVisible || isAtTop) && !overlayHeader,
          [styles.transitionDisabled]: isTransitionDisabled,
          [styles.visibleAsOverlay]: isVisibleOverlay,
          [styles.fixed]: shouldAddFixedStyle,
        })}
        id={PAGE_HEADER_ID}
        data-testid="header"
        ref={headerRef}
      >
        <div className={styles.container}>
          <div className={styles.desktopLogo}>
            <Logo isDarkMode={true} {...logo} className={classNames({ [logoStyles.hidden]: theme !== "dark" })} />
            <Logo isDarkMode={false} {...logo} className={classNames({ [logoStyles.hidden]: theme === "dark" })} />
          </div>
          <div className={styles.content}>
            <div className={styles.mobileLogo}>
              <Logo isDarkMode={true} {...logo} className={classNames({ [logoStyles.hidden]: theme !== "dark" })} />
              <Logo isDarkMode={false} {...logo} className={classNames({ [logoStyles.hidden]: theme === "dark" })} />
            </div>
            {!isSimplified && (
              <div className={styles.tools} data-testid="header-tools">
                <Tools
                  accountLink={accountLink}
                  accountLabel={accountLabel}
                  accountLoadingLabel={accountLoadingLabel}
                  accountDropdownSettings={accountDropdownSettings}
                  renderMiniCart={renderMiniCart}
                  mobileAppLink={mobileAppLink}
                  storeLocatorLink={storeLocatorLink}
                  storeLocatorLabel={storeLocatorLabel}
                  resultsLink={resultsLink}
                  onCloseSearch={closeSearch}
                  onOpenSearch={openSearch}
                  isSearchOpened={isSearchOpened}
                  currentPathname={currentPathname}
                  withCart={withCart}
                  onLogOut={onLogOut}
                  tabletUserNameRef={tabletUserNameRef}
                  isEnabledUserIndication={isEnabledUserIndication}
                  isEnabledUserNameForIndication={isEnabledUserNameForIndication}
                />
                <div className={styles.desktopLanguageSelector} data-testid="desktopLanguageSelector">
                  <LanguageSelector
                    languages={languages}
                    popupContext="NAVIGATION"
                    variant={variant.desktop ?? "SHORT_LANGUAGE_NAME"}
                  />
                </div>
              </div>
            )}
            {!hideNavigation && !isSimplified && (
              <>
                <button
                  className={classNames(styles.burgerMenu, {
                    [styles.hamburgerExpanded]: navigationOpened,
                  })}
                  onClick={handleHamburgerClick}
                  aria-expanded={navigationOpened}
                  aria-controls="header-navigation"
                  aria-label={navigationOpened ? closeMenuLabel : openMenuLabel}
                >
                  <BurgerMenuIcon className={styles.burgerMenuIcon} />
                </button>
                <div
                  id="header-navigation"
                  className={classNames(styles.navigation, { [styles.navigationExpanded]: navigationOpened })}
                >
                  {isEnabledUserIndication && isEnabledUserNameForIndication && <UserIndicationButtonMobile />}
                  <div className={styles.navigationContent}>
                    <Navigation navLinks={navLinks} renderSubmenu={renderSubmenu} storeLocatorLink={storeLocatorLink} />
                    <div className={styles.navigationBottomSection}>
                      <div className={styles.mobileLanguageSelector} data-testid="mobileLanguageSelector">
                        <LanguageSelector
                          languages={languages}
                          popupContext="NAVIGATION"
                          variant={variant.mobile ?? "FULL_LANGUAGE_NAME"}
                        />
                      </div>
                      {isUserLoggedIn && (
                        <SignOut label={accountDropdownSettings?.logoutLabel ?? ""} onLogOut={onLogOut} />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {isEnabledUserIndication && isEnabledUserNameForIndication && (
            <div ref={tabletUserNameRef} className={styles.tabletUserName} />
          )}
        </div>
        <div className={styles.searchLayerContainer}>
          <SearchHeaderBanner
            isOpened={isSearchOpened}
            onClose={closeSearch}
            resultsPageUrl={resultsLink ?? ""}
            Container={Container}
          />
        </div>
        <div className={classNames({ [styles.hideBreadcrumbs]: hideBreadcrumbs })}>
          <HeaderBreadcrumbs
            breadcrumbsList={breadcrumbsList}
            useHeadingTagForBreadcrumbs={useHeadingTagForBreadcrumbs}
            expandBreadcrumbsLabel={expandBreadcrumbsLabel}
          />
        </div>
        <div
          className={classNames({
            [styles.headerPortalHidden]: navigationOpened,
          })}
          ref={setHeaderPortalRef}
          data-portal="header"
        ></div>
      </header>
    </HeaderProvider>
  );
};
